import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { getQuotes, getFxTable, getHistory, keyOf, loadPriceBook, marketOpenFor } from "../marketData.js";
import { rate, guessCurrency } from "./fx.js";
import { summarize, combine, benchmark as runBenchmark, holdingTx } from "./calc.js";
import { netWorthTotals, snapshotPoint, snapshotDue, maybeSnapshot, chartSeries, isoToday } from "./networth.js";
import { buildItems, groupBy, concentration, CLASSES, SECTORS, REGIONS, DEFAULT_LIMITS } from "./allocation.js";

export const WIDGETS = [
  ["networth", "Net worth"], ["today", "Today's change"], ["movers", "Top movers"], ["performance", "Performance"],
  ["allocation", "Allocation"], ["risk", "Concentration check"], ["alerts", "Price alerts"],
];
export const LOCAL_INDEX = { AU: ["IOZ.AX", "ASX 200"], NZ: ["FNZ.NZ", "NZX 50"], CA: ["XIU.TO", "TSX 60"] };
export const benchmarkOptions = (country) => [["SPY", "S&P 500"], ...(LOCAL_INDEX[country] ? [LOCAL_INDEX[country]] : [])];

export function dashLayout(saved) {
  const ids = WIDGETS.map((w) => w[0]);
  const order = [...(saved?.order || []).filter((i) => ids.includes(i)), ...ids.filter((i) => !(saved?.order || []).includes(i))];
  return { order, hidden: (saved?.hidden || []).filter((i) => ids.includes(i)) };
}

// quotes restored from the price book are shown straight away, marked as saved
const fromBook = () => Object.fromEntries(Object.entries(loadPriceBook()).map(([k, q]) => [k, { ...q, stale: true }]));

export function useInvestData({ profile, update, toast, fmt, country }) {
  const cur = profile.currency || "USD";
  const settings = { cost: "avg", benchmark: "SPY", cadence: "monthly", ...DEFAULT_LIMITS, ...(profile.invSettings || {}) };
  const [fx, setFx] = useState(null);
  const [quotes, setQuotes] = useState(fromBook);
  const [status, setStatus] = useState("live");
  const [errors, setErrors] = useState({});
  const [loadedAt, setLoadedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const live = useRef(profile); live.current = profile;

  const fmtIn = useCallback((n, ccy) => {
    if (!ccy || ccy === cur) return fmt(n);
    try { return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(n); } catch (e) { return `${ccy} ${n.toFixed(2)}`; }
  }, [cur, fmt]);

  const refresh = useCallback(async (silent) => {
    const p = live.current;
    const items = [...(p.investments || []), ...(p.watchlist || [])].map((x) => ({ symbol: x.symbol, assetType: x.assetType, cgId: x.cgId }));
    if (!silent) setRefreshing(true);
    const [table, r] = await Promise.all([getFxTable(), items.length ? getQuotes(items) : Promise.resolve({ quotes: {}, errors: {}, status: "live" })]);
    if (table) setFx(table);
    setQuotes((q) => ({ ...q, ...r.quotes })); setStatus(r.status); setErrors(r.errors || {}); setLoadedAt(Date.now());
    if (!silent) { setRefreshing(false); toast?.(r.status === "live" ? "Prices updated" : r.status === "partial" ? "Updated what it could" : r.status === "setup" ? "Stock prices aren't set up yet" : "You're offline — showing saved prices"); }
  }, [toast]);

  // One-time upgrade of holdings saved by the old single-price form: record the buy as a transaction and pin its currency
  // (it was entered in the profile currency), so later currency changes can't reinterpret it. Old fields are left in place.
  useEffect(() => {
    if ((live.current.investments || []).some((h) => !h.currency || !Array.isArray(h.tx))) {
      update((p) => ({ ...p, investments: (p.investments || []).map((h) => (h.currency && Array.isArray(h.tx) ? h : { ...h, currency: h.currency || p.currency || "USD", tx: holdingTx(h) })) }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // first load, then every 5 minutes while any holding's market is open
  useEffect(() => { refresh(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const id = setInterval(() => { const p = live.current; if ([...(p.investments || []), ...(p.watchlist || [])].some((x) => x.assetType === "crypto" || marketOpenFor(x.symbol))) refresh(true); }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [refresh]);

  const holdings = useMemo(() => (profile.investments || []).map((h) => {
    const q = quotes[keyOf(h)];
    const hc = h.currency || cur;                       // legacy holdings were entered in the profile currency
    const qc = q?.currency || guessCurrency(h.symbol, h.assetType);
    const r1 = rate(hc, cur, fx), r2 = rate(qc, cur, fx);
    const sum = summarize(h, { quote: q, fxNow: r1 ?? 1, fxPrice: r2 ?? 1, method: settings.cost });
    return { ...h, tx: holdingTx(h), hc, qc, fxPrice: r2 ?? 1, quote: q, sum, fxMissing: r1 === null || r2 === null ? [hc, qc].filter((c) => c !== cur && rate(c, cur, fx) === null) : [], error: errors[keyOf(h)] };
  }), [profile.investments, quotes, fx, cur, settings.cost, errors]); // eslint-disable-line react-hooks/exhaustive-deps

  const port = useMemo(() => combine(holdings.map((h) => h.sum)), [holdings]);
  const nw = useMemo(() => netWorthTotals(profile, { investedValue: port.value, fxTable: fx }), [profile.accounts, profile.liabilities, profile.currency, port.value, fx]); // eslint-disable-line react-hooks/exhaustive-deps
  const items = useMemo(() => buildItems(holdings, nw.accounts), [holdings, nw.accounts]);
  const groups = useMemo(() => ({ cls: groupBy(items, "cls", CLASSES), sector: groupBy(items.filter((i) => i.kind === "holding"), "sector", SECTORS), region: groupBy(items.filter((i) => i.kind === "holding"), "region", REGIONS) }), [items]);
  const warnings = useMemo(() => concentration(items, settings), [items, settings.maxHolding, settings.maxAsset, settings.maxSector, settings.maxRegion]); // eslint-disable-line react-hooks/exhaustive-deps
  const fxMissing = [...new Set([...nw.missingFx, ...holdings.flatMap((h) => h.fxMissing)])];

  // ---- net worth snapshots: one per week or month, taken when the page opens and prices have loaded
  useEffect(() => {
    if (!loadedAt || fxMissing.length) return;
    if (!(nw.accounts.length || nw.debts.length || holdings.length)) return;
    const pt = snapshotPoint(nw, cur);
    if (snapshotDue(live.current.nwSnapshots || [], settings.cadence, pt.d)) update((p) => ({ ...p, nwSnapshots: maybeSnapshot(p.nwSnapshots || [], pt, settings.cadence) }));
  }, [loadedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const livePoint = useMemo(() => snapshotPoint(nw, cur), [nw, cur]);
  const series = useCallback((range) => chartSeries(profile.nwSnapshots || [], livePoint, range).map((s) => ({ t: s.d, ...s })), [profile.nwSnapshots, livePoint]);

  // ---- price alerts (fire while the app is open; stale prices never trigger one)
  useEffect(() => {
    const hits = [];
    (live.current.watchlist || []).forEach((w) => {
      const q = quotes[keyOf(w)];
      if (!q || q.stale) return;
      (w.alerts || []).forEach((a) => { if (!a.hit && (a.dir === "above" ? q.price >= a.price : q.price <= a.price)) hits.push({ w, a, q }); });
    });
    if (!hits.length) return;
    const ids = new Set(hits.map((h) => h.a.id)), at = new Date().toISOString();
    update((p) => ({ ...p, watchlist: (p.watchlist || []).map((w) => ({ ...w, alerts: (w.alerts || []).map((a) => (ids.has(a.id) && !a.hit ? { ...a, hit: { at, price: quotes[keyOf(w)]?.price } } : a)) })) }));
    hits.forEach(({ w, a, q }) => {
      const msg = `${w.symbol} ${a.dir === "above" ? "rose to" : "fell to"} ${fmtIn(q.price, q.currency)} (alert ${a.dir} ${fmtIn(a.price, q.currency)})`;
      toast?.(msg);
      try { if ("Notification" in window && Notification.permission === "granted") new Notification("Price alert", { body: msg }); } catch (e) { /* notifications are optional */ }
    });
  }, [quotes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- benchmark: the same cash flows put into an index fund instead
  const [bench, setBench] = useState({ series: null, loading: false, error: null });
  const firstFlow = port.flows.reduce((m, f) => (!m || f.t < m ? f.t : m), null);
  const range = firstFlow && (Date.parse(isoToday()) - Date.parse(firstFlow)) / 86400000 > 360 ? "5Y" : "1Y";
  useEffect(() => {
    if (!firstFlow) { setBench({ series: null, loading: false, error: null }); return; }
    let ok = true;
    setBench((b) => ({ ...b, loading: true }));
    getHistory({ symbol: settings.benchmark, assetType: "stock" }, range).then((h) => { if (ok) setBench({ series: h.points?.length ? h.points : null, loading: false, error: h.points?.length ? null : h.error || "No index data" }); });
    return () => { ok = false; };
  }, [settings.benchmark, range, !!firstFlow, loadedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const benchResult = useMemo(() => (bench.series ? runBenchmark(port.flows, bench.series) : null), [bench.series, port.flows]);
  const benchLabel = (benchmarkOptions(country).find((o) => o[0] === settings.benchmark) || [settings.benchmark, settings.benchmark])[1];

  const setSettings = (patch) => update((p) => ({ ...p, invSettings: { ...(p.invSettings || {}), ...patch } }));
  const dash = dashLayout(profile.invDash);
  const saveDash = (d) => update((p) => ({ ...p, invDash: d }));

  return { cur, fmt, fmtIn, country, settings, setSettings, fx, quotes, status, errors, loadedAt, refreshing, refresh, holdings, port, nw, items, groups, warnings, fxMissing, series, bench: { ...bench, result: benchResult, label: benchLabel, symbol: settings.benchmark }, dash, saveDash, update, toast, profile };
}
